<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="text-center">
        <v-toolbar-title>Project: {{ currentProjectName }}</v-toolbar-title>
      </v-col>
    </v-row>
    <!-- Accounts -->
    <v-row>
      <v-col cols="12" sm="6" md="3">
        <v-toolbar-title>Accounts</v-toolbar-title>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" sm="6" md="3">
        <v-btn large block color="success" @click="openAccountDialog"
          >Add account</v-btn
        >
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-alert
          dense
          outlined
          type="error"
          v-show="accountDeleteErrors.length"
        >
          <ul>
            <li v-for="error in accountDeleteErrors" :key="error">
              {{ error }}
            </li>
          </ul>
        </v-alert>
        <v-alert
          v-model="showAccountSuccessAlert"
          type="success"
          dismissible
          close-icon="mdi-delete"
          outlined
        >
          Account was created successfully!
        </v-alert>
        <v-alert
          v-model="showAccountUpdateAlert"
          type="success"
          dismissible
          close-icon="mdi-delete"
          outlined
        >
          Account was updated successfully!
        </v-alert>
        <v-alert
          v-model="showAccountDeleteSuccessAlert"
          type="success"
          dismissible
          close-icon="mdi-delete"
          outlined
        >
          Account was deleted successfully!
        </v-alert>
        <v-data-table
          id="accounts-table"
          :item-class="itemRowBackground"
          :headers="accountHeaders"
          :items="projectAccounts"
          :items-per-page="-1"
          hide-default-footer
          class="elevation-2"
        >
          <template #item.expand="{ item }">
            <v-icon
              v-show="item.children.length"
              small
              @click="expandChildren(item)"
              :title="item.expanded ? 'Collapse' : 'Expand'"
              :class="{ 'success--text': item.expanded }"
            >
              {{ item.expanded ? "mdi-arrow-up-thin" : "mdi-arrow-down-thin" }}
            </v-icon>
          </template>
          <template v-slot:item.name="{ item }">
            {{ item.name }}
            <!-- <br /> -->
            <v-icon
              v-if="
                item.level < 3 &&
                  !item.childless &&
                  (!item.transactable || item.amount === 0)
              "
              class="mr-2 add-icon"
              small
              title="Add child"
              @click="openAccountDialog(item)"
            >
              mdi-plus
            </v-icon>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-icon
              v-show="item.is_owner && !item.is_main"
              small
              class="mr-2"
              @click="setEditAccount(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              v-show="item.is_owner && !item.is_main"
              small
              class="mr-2"
              @click="confirmDeleteAccount(item)"
            >
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-dialog v-model="accountDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ accountModalTitle }}</v-card-title>
        <v-card-text>
          <v-alert dense outlined type="error" v-show="accountErrors.length">
            <ul>
              <li v-for="error in accountErrors" :key="error">
                {{ error }}
              </li>
            </ul>
          </v-alert>
          <v-form>
            <v-select
              v-show="false"
              v-model="accountModel.project_id"
              :items="projects"
              :menu-props="{ maxHeight: '400' }"
              label="Project *"
              :item-text="(item) => item.name"
              :item-value="(item) => item.project_id"
              persistent-hint
            >
            </v-select>
            <v-text-field
              label="Name *"
              v-model="accountModel.name"
            ></v-text-field>
            <v-text-field
              v-show="!isAccountEdit"
              label="Amount"
              type="number"
              v-model="accountModel.amount"
              :disabled="isAmountReadonly"
            ></v-text-field>
            <v-select
              v-model="accountModel.parent_id"
              :items="modelAccounts"
              :menu-props="{ maxHeight: '400' }"
              label="Parent *"
              :item-text="(item) => item.name"
              :item-value="(item) => item.id"
              persistent-hint
              :disabled="accountModel.has_transactions"
            >
            </v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="accountDialog = false">Close</v-btn>
          <v-btn color="primary" @click="updateAccount">{{
            accountModalActionButton
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="deleteAccountDialog" max-width="290">
      <v-card>
        <v-card-title
          v-if="!this.accountModel.has_transactions"
          class="headline"
          >Please confirm</v-card-title
        >
        <v-card-text v-if="!this.accountModel.has_transactions">
          You are about to delete this account.
        </v-card-text>

        <v-card-title v-if="this.accountModel.has_transactions" class="headline"
          >Cannot delete</v-card-title
        >
        <v-card-text v-if="this.accountModel.has_transactions">
          This account has transactions associated with it and therefore cannot
          be deleted. Please, move all transactions to a different account and
          try again.</v-card-text
        >

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn text @click="deleteAccountDialog = false"> Close </v-btn>

          <v-btn
            v-if="!this.accountModel.has_transactions"
            color="green darken-1"
            text
            @click="deleteAccount"
          >
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End Accounts -->

    <!-- Categories-->
    <v-row>
      <v-col cols="12" sm="6" md="3">
        <v-toolbar-title>Categories</v-toolbar-title>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" sm="6" md="3">
        <v-btn large block color="success" @click="openCategoryDialog"
          >Add category</v-btn
        >
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-alert
          v-model="showCategoryDeleteErrors"
          dense
          outlined
          dismissible
          close-icon="mdi-delete"
          type="error"
        >
          <ul>
            <li v-for="error in categoryDeleteErrors" :key="error">
              {{ error }}
            </li>
          </ul>
        </v-alert>
        <v-alert
          v-model="showCategorySuccessAlert"
          type="success"
          dismissible
          close-icon="mdi-delete"
          outlined
        >
          Transaction category was created successfully!
        </v-alert>
        <v-alert
          v-model="showCategoryUpdateAlert"
          type="success"
          dismissible
          close-icon="mdi-delete"
          outlined
        >
          Transaction category was updated successfully!
        </v-alert>
        <v-alert
          v-model="showCategoryDeleteSuccessAlert"
          type="success"
          dismissible
          close-icon="mdi-delete"
          outlined
        >
          Transaction category was deleted successfully!
        </v-alert>
        <v-treeview hoverable activatable :items="projectCategories">
          <template v-slot:append="{ item }">
            <v-btn
              v-if="!item.is_main"
              color="#D50000"
              outlined
              class="mr-2"
              small
              @click="deleteSubCategory(item)"
            >
              <v-icon small @click="deleteSubCategory(item)">
                mdi-delete
              </v-icon>
            </v-btn>
            <v-btn
              v-if="!item.is_main"
              color="#success"
              outlined
              class="mr-2"
              small
              @click="editSubCategory(item)"
            >
              <v-icon small @click="editSubCategory(item)"> mdi-pencil </v-icon>
            </v-btn>
            <v-btn color="success" outlined small @click="addSubCategory(item)"
              >+
            </v-btn>
            <v-icon
              v-show="item.is_owner"
              small
              class="mr-2"
              @click="setEditAccount(item)"
            >
              mdi-pencil
            </v-icon>
          </template>
        </v-treeview>
      </v-col>
    </v-row>
    <v-dialog v-model="categoryDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ transactionCategoryModalTitle }}</v-card-title>
        <v-card-text>
          <v-alert dense outlined type="error" v-show="categoryErrors.length">
            <ul>
              <li v-for="error in categoryErrors" :key="error">
                {{ error }}
              </li>
            </ul>
          </v-alert>
          <v-form>
            <v-select
              v-show="!isCategoryEdit"
              v-model="categoryModel.type"
              :items="transactionCategoriesTypes"
              :menu-props="{ maxHeight: '400' }"
              label="Type *"
              :item-text="(item) => item.name"
              :item-value="(item) => item.id"
              persistent-hint
            >
            </v-select>
            <v-text-field
              label="Name *"
              v-model="categoryModel.name"
            ></v-text-field>
            <v-select
              v-model="categoryModel.parent_category_id"
              :items="categoriesDialog"
              :menu-props="{ maxHeight: '400' }"
              label="Parent category"
              :item-text="(item) => item.name"
              :item-value="(item) => item.id"
              persistent-hint
            >
            </v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="categoryDialog = false">Close</v-btn>
          <v-btn color="primary" @click="updateTransactionCategory">
            {{ transactionCategoryModalActionButton }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="categoryDeleteDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Please confirm</v-card-title>

        <v-card-text> You are about to delete this category. </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn text @click="categoryDeleteDialog = false"> Close </v-btn>

          <v-btn color="green darken-1" text @click="deleteTransactionCategory">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- End Categories -->
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import AccountModel from "../models/accounts/account";
import UserAccountModel from "../models/accounts/user_account";
import TransactionCategoryModel from "../models/categories/transaction_category";
import BaseService from "../services/BaseService";

export default {
  data() {
    return {
      // Account
      isAmountReadonly: false,
      accountModel: new AccountModel(),
      userAccountModel: new UserAccountModel(),
      accountDialog: false,
      isAccountEdit: false,
      showAccountSuccessAlert: false,
      showAccountDeleteSuccessAlert: false,
      showAccountUpdateAlert: false,
      deleteAccountDialog: false,
      accountUsersDialog: false,
      accountUsers: [],
      accountHeaders: [
        // { text: "ID", value: "id" },
        { text: "", value: "expand", sortable: false },
        { text: "Name", value: "name" },
        { text: "Amount", value: "amount_show" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      accountUserHeaders: [
        { text: "First name", value: "first_name" },
        { text: "Last name", value: "last_name" },
        { text: "Email", value: "email" },
        { text: "Actions", value: "actions", sortable: false },
      ],
      accountErrors: [],
      accountDeleteErrors: [],
      projectAccounts: [],
      // End Account

      // Category
      transactionCategoriesTypes: [
        {
          id: "income",
          name: "Income",
        },
        {
          id: "expense",
          name: "Expense",
        },
        {
          id: "transfer",
          name: "Transfer",
        },
      ],
      categoryDeleteDialog: false,
      categoryModel: new TransactionCategoryModel(),
      showCategoryDeleteErrors: false,
      categoryDialog: false,
      showCategorySuccessAlert: false,
      showCategoryDeleteSuccessAlert: false,
      showCategoryUpdateAlert: false,
      deleteCategoryDialog: false,
      isCategoryEdit: false,
      categoryErrors: [],
      categoryDeleteErrors: [],
      categoryHeaders: [
        { text: "ID", value: "id" },
        { text: "Name", value: "name" },
        { text: "Type", value: "type" },
        // {text: 'Parent ID', value: 'parent_category_id'},
        { text: "Actions", value: "actions", sortable: false },
      ],
      // End Category
    };
  },
  mounted() {
    this.$store.dispatch("loadUsers");
    this.resetAccountModel();
    this.resetCategoryModel();
    this.projectAccounts = this.accounts
      .filter(
        (account) =>
          account.project_id === this.accountModel.project_id &&
          account.parent_id === null
      )
      .map((item) => {
        item.expanded = false;
        return item;
      });
  },
  watch: {
    "accountModel.parent_id": function() {
      this.updateAmountReadonlyStatus();
    },
    accountDialog(newValue) {
      if (!newValue) {
        this.resetAccountModel();
      }
    },
    accounts() {
      this.projectAccounts = this.accounts
        .filter(
          (account) =>
            account.project_id === this.accountModel.project_id &&
            account.parent_id === null
        )
        .map((item) => {
          item.expanded = false;
          return item;
        });
    },
  },
  computed: {
    ...mapGetters([
      "accounts",
      "projects",
      "users",
      "transactionTreeViewCategories",
      "transactionCategories",
    ]),
    modelAccounts() {
      const that = this;

      if (!that) return;
      let qualifyingAccounts = [];

      function traverse(accounts) {
        for (const account of accounts) {
          // Check if the account meets the specified condition
          if (
            account.level < 3 &&
            !account.childless &&
            (account.amount === 0 || !account.transactable) &&
            account.project_id === that.accountModel.project_id
          ) {
            qualifyingAccounts.push(account);
          }

          // If the account has children, traverse them recursively
          if (account.children && account.children.length > 0) {
            traverse(account.children);
          }
        }
      }

      traverse(this.accounts);
      return qualifyingAccounts;
    },
    projectCategories() {
      let copy = JSON.parse(JSON.stringify(this.transactionTreeViewCategories));

      return copy.filter((category) => {
        this.extractChildCategories(category);

        return (
          category.project_id === this.accountModel.project_id ||
          (category.project_id === null &&
            this.currentProject &&
            category.project_type === this.currentProject.type)
        );
      });
    },
    categoriesDialog() {
      let copy = JSON.parse(JSON.stringify(this.transactionCategories));

      copy = copy.filter((category) => {
        let filter = true;
        if (this.categoryModel.type) {
          filter = category.type == this.categoryModel.type;
        }

        return (
          filter &&
          !category.childless &&
          (category.project_id === this.accountModel.project_id ||
            (category.project_id === null &&
              this.currentProject &&
              category.project_type === this.currentProject.type))
        );
      });

      return copy;
    },
    // Account
    accountModalTitle() {
      if (this.isAccountEdit) {
        return "Edit account: " + this.accountModel.name;
      }
      return "Add new account";
    },
    accountModalActionButton() {
      if (this.isAccountEdit) {
        return "Update";
      }
      return "Add";
    },
    currentProject() {
      if (this.projects.length && this.accountModel.project_id) {
        return this.projects.filter(
          (project) => project.project_id === this.accountModel.project_id
        )[0];
      }

      return null;
    },
    currentProjectName() {
      let project = this.currentProject;

      if (project !== null) {
        return project.name;
      }
      return "";
    },
    // End Account

    // Category
    transactionCategoryModalTitle() {
      if (this.isCategoryEdit) {
        return "Edit transaction category: " + this.categoryModel.name;
      }
      return "Add new transaction category";
    },
    transactionCategoryModalActionButton() {
      if (this.isCategoryEdit) {
        return "Update";
      }
      return "Add";
    },
    // End Category
  },
  methods: {
    updateAmountReadonlyStatus() {
      // Find the selected parent account based on accountModel.parent_id
      const parentAccount = this.projectAccounts.find(
        (account) => account.id === this.accountModel.parent_id
      );
      if (parentAccount && parentAccount.is_liability) {
        // If the parent account is a liability, set amount to 0 and readonly
        this.accountModel.amount = 0;
        this.isAmountReadonly = true;
      } else {
        // Otherwise, ensure the amount is not readonly
        // You may adjust this behavior based on your requirements
        this.isAmountReadonly = false;
      }
    },
    // Account
    expandChildren(item) {
      // Make a deep copy of the current project accounts to avoid mutating state directly
      let copy = JSON.parse(JSON.stringify(this.projectAccounts));

      // Find the index of the item to expand/collapse
      let index = copy.findIndex((el) => el.id == item.id);

      // Toggle the expanded state of the item
      copy[index].expanded = !copy[index].expanded;

      if (copy[index].expanded) {
        // If expanding, insert the children directly after the parent item
        let insertIndex = index + 1;
        for (const child of item?.children || []) {
          copy.splice(insertIndex, 0, child);
          insertIndex++;
        }
      } else {
        // If collapsing, remove all direct children and any expanded sub-children
        const removeChildren = (parentId, startIndex) => {
          let endIndex = startIndex;
          while (
            endIndex < copy.length &&
            copy[endIndex].parent_id === parentId
          ) {
            if (copy[endIndex].expanded) {
              // Recursively remove expanded children of this child
              endIndex = removeChildren(copy[endIndex].id, endIndex + 1);
            } else {
              endIndex++;
            }
          }
          copy.splice(startIndex, endIndex - startIndex);
          return startIndex; // Return the new index after removal
        };

        // Start removing from the next element after the parent
        removeChildren(item.id, index + 1);
      }

      // Update the projectAccounts with the modified copy
      this.projectAccounts = copy;
    },

    openAccountDialog(item = null) {
      this.resetAccountModel();

      if (item) {
        this.accountModel.parent_id = item.id;
      }
      this.isAccountEdit = false;
      this.accountDialog = true;
    },
    resetAccountModel() {
      this.accountModel = new AccountModel();
      this.accountModel.project_id = Number(this.$route.params.id);
      this.accountErrors = [];
    },
    deleteUserFromAccount(item) {
      this.$store
        .dispatch("deleteUserFromAccount", item.account_id)
        .then(() => {
          this.accountUsersDialog = false;
          this.showUpdateAlert = true;
          this.isEdit = false;
          this.resetModel();
        })
        .catch((result) => {
          this.parseAccountErrors(result);
        });
    },
    addUserToAccount() {
      this.$store
        .dispatch("addUserToAccount", this.userAccountModel)
        .then(() => {
          this.accountUsersDialog = false;
          this.showUseUpdateAlert = true;
          this.isEdit = false;
          this.resetModel();
        })
        .catch((result) => {
          this.parseAccountErrors(result);
        });
    },
    updateAccount() {
      if (this.isAccountEdit) {
        this.$store
          .dispatch("updateAccount", {
            id: this.accountModel.id,
            data: this.accountModel,
          })
          .then(() => {
            this.accountDialog = false;
            this.showAccountUpdateAlert = true;
            this.isAccountEdit = false;
            this.resetAccountModel();
          })
          .catch((result) => {
            this.parseAccountErrors(result);
          });
      } else {
        this.$store
          .dispatch("createAccount", this.accountModel)
          .then(() => {
            this.accountDialog = false;
            this.showAccountSuccessAlert = true;
            this.resetAccountModel();
          })
          .catch((result) => {
            this.parseAccountErrors(result);
          });
      }
    },
    setEditAccount(item) {
      this.accountModel = item;
      this.accountDialog = true;
      this.isAccountEdit = true;
    },
    confirmDeleteAccount(item) {
      this.accountModel = item;
      this.deleteAccountDialog = true;
    },
    deleteAccount() {
      this.$store
        .dispatch("deleteAccount", this.accountModel.id)
        .then(() => {
          this.showAccountDeleteSuccessAlert = true;
          this.deleteAccountDialog = false;
          this.resetAccountModel();
        })
        .catch((result) => {
          var errors = result.response.data.errors;
          let keys = Object.keys(errors);
          this.deleteErrors = [];

          keys.forEach((key) => {
            let item = errors[key];
            item.forEach((error) => {
              this.accountDeleteErrors.push(error);
            });
          });
        });
    },
    openAccountUsersDialog(item) {
      this.accountUsersDialog = true;
      this.accountUsers = item.users;
      this.account_id = item.id;
      this.userAccountModel.account_id = item.id;
      this.userAccountModel.user_id = this.user_id;
    },
    parseAccountErrors(result) {
      if (result.response.status === BaseService.STATUS_403) {
        this.showAccountDeleteErrors = true;
        this.accountDeleteErrors = [
          "You have no permission to delete this аccount",
        ];
        this.accountDeleteDialog = false;
        return;
      }
      var errors = result.response.data.errors;
      let keys = Object.keys(errors);
      this.accountErrors = [];

      keys.forEach((key) => {
        let item = errors[key];
        item.forEach((error) => {
          this.accountErrors.push(error);
        });
      });
    },
    // End Account

    // Category
    resetCategoryModel() {
      this.categoryModel = new TransactionCategoryModel();
      this.categoryModel.project_id = Number(this.$route.params.id);
      this.categoryModel.project_type =
        this.currentProject && this.currentProject.type;
    },
    openCategoryDialog() {
      this.categoryDialog = true;
      this.isCategoryEdit = false;
      this.resetCategoryModel();
    },
    updateTransactionCategory() {
      if (this.isCategoryEdit) {
        this.$store
          .dispatch("updateTransactionCategory", {
            id: this.categoryModel.id,
            data: this.categoryModel,
          })
          .then(() => {
            this.categoryDialog = false;
            this.showCategoryUpdateAlert = true;
            this.isCategoryEdit = false;
            this.resetCategoryModel();
          })
          .catch((result) => {
            this.parseCategoryErrors(result);
          });
      } else {
        this.$store
          .dispatch("createTransactionCategory", this.categoryModel)
          .then(() => {
            this.categoryDialog = false;
            this.showCategorySuccessAlert = true;
            this.resetCategoryModel();
          })
          .catch((result) => {
            this.parseCategoryErrors(result);
          });
      }
    },
    parseCategoryErrors(result) {
      if (result.response.status === BaseService.STATUS_403) {
        this.showCategoryDeleteErrors = true;
        this.categoryDeleteErrors = [
          "You have no permission to delete this category",
        ];
        this.categoryDeleteModal = false;
        return;
      }
      var errors = result.response.data.errors;
      let keys = Object.keys(errors);
      this.categoryErrors = [];

      keys.forEach((key) => {
        let item = errors[key];
        item.forEach((error) => {
          this.categoryErrors.push(error);
        });
      });
    },
    setEditTransactionCategory(item) {
      this.categoryModel = item;
      this.isCategoryEdit = true;
      this.categoryDialog = true;
    },
    confirmDeleteTransactionCategory(item) {
      this.categoryModel = item;
      this.categoryDeleteDialog = true;
    },
    deleteTransactionCategory() {
      this.$store
        .dispatch("deleteTransactionCategory", this.categoryModel.id)
        .then(() => {
          this.showCategoryDeleteSuccessAlert = true;
          this.categoryDeleteDialog = false;
          this.resetCategoryModel();
        })
        .catch((result) => {
          this.parseCategoryErrors(result);
          this.categoryDeleteDialog = false;
          this.$store.dispatch("loadTransactionTreeViewCategories", true);
        });
    },
    addSubCategory(item) {
      this.categoryModel = JSON.parse(JSON.stringify(item));
      this.categoryModel.project_id = Number(this.$route.params.id);
      this.categoryModel.parent_category_id = this.categoryModel.id;
      this.categoryModel.name = "";
      this.categoryModel.id = null;
      this.isCategoryEdit = false;
      this.categoryDialog = true;
    },
    editSubCategory(item) {
      this.categoryModel = item;
      this.isCategoryEdit = true;
      this.categoryDialog = true;
    },
    deleteSubCategory(item) {
      this.categoryModel = item;
      this.categoryDeleteDialog = true;
    },
    extractChildCategories(category) {
      let children = [];
      Array.from(category.children).forEach((child) => {
        if (
          child.project_id === this.accountModel.project_id ||
          child.project_id === null
        ) {
          children.push(child);
        }

        if (child.children) {
          this.extractChildCategories(child);
        }
      });

      if (
        category.project_id === this.accountModel.project_id ||
        category.project_id === null
      ) {
        category.children = children;
      }
    },
    itemRowBackground: function(item) {
      if (item.level == 1) {
        return "first-lvl-acc";
      } else if (item.level == 2) {
        return "second-lvl-acc";
      } else {
        return "third-lvl-acc";
      }
    },
  },
};
</script>

<style>
#accounts-table th:first-child,
#accounts-table td:first-child {
  width: 16px;
  white-space: nowrap;
  padding-right: 0;
}

#accounts-table tr.second-lvl-acc td:nth-child(1),
#accounts-table tr.second-lvl-acc td:nth-child(2),
#accounts-table tr.second-lvl-acc td:nth-child(3) {
  padding-left: 40px;
}

#accounts-table tr.third-lvl-acc td:nth-child(1),
#accounts-table tr.third-lvl-acc td:nth-child(2),
#accounts-table tr.third-lvl-acc td:nth-child(3) {
  padding-left: 80px;
}

#accounts-table td:nth-child(3),
#accounts-table th:nth-child(3) {
  min-width: 20px;
  width: auto !important;
}

#accounts-table .mr-2.add-icon {
  visibility: hidden;
  padding-bottom: 5px;
  border-top: 1px solid lightgray;
  border-radius: 10px 10px 0 0;
  padding-bottom: 0 !important;
}

#accounts-table td:hover .mr-2.add-icon {
  visibility: visible;
}

#accounts-table td:nth-child(4),
#accounts-table th:nth-child(4) {
  width: 80px;
}

@media screen and (max-width: 992px) {
  #accounts-table th:nth-child(2) {
    width: 45%;
  }
}
</style>
